//src/pages/CoursePage

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../api/api";
import YouTubeEmbed from "../components/common/YouTubeEmbed";
import Spinner from "../components/common/Spinner";
import { useAuth } from "../contexts/AuthContext";
import AppLayout from "../components/layout/AppLayout";
import { formatDistanceToNow, parseISO, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { PlayIcon, CheckCircleIcon, LockClosedIcon } from "@heroicons/react/24/solid";
import VideoUploadPlayer from "../components/lesson/VideoUploadPlayer";
const BACKEND_URL = "https://imagensdaface.com.br";

const fileURL = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}/${path.replace(/^\//,"")}`;
};

async function completeLesson(lessonId) {
  try {
    const res = await api.post(`/lessons/${lessonId}/track`, { action: "complete" });
    return res.data.completed;
  } catch (err) { console.error(err); return false; }
}

/* util shuffle */
function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ============================================================
COMPONENT PRINCIPAL - CoursePage
============================================================*/
export default function CoursePage() {
  const { courseId } = useParams();
  const { user } = useAuth();

  const [course, setCourse] = useState(null);
  const [activeModule, setActiveModule] = useState(0);
  const [activeLesson, setActiveLesson] = useState(0);

  const [completedLessons, setCompletedLessons] = useState({});
  const [quizApproved, setQuizApproved] = useState({});
  const [loading, setLoading] = useState(true);

  const [showQuiz, setShowQuiz] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showQuizHint, setShowQuizHint] = useState(false);

  const lessonContentRef = useRef(null);
  const [canFinish, setCanFinish] = useState(false);
  const [isAutoVideoDone, setIsAutoVideoDone] = useState(false);

  /* Load course */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get(`/courses/${courseId}`);
        if (!mounted) return;
        setCourse(r.data.course);
        setCompletedLessons(r.data.course.completedLessons || {});
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [courseId]);

  /* Carrega status de quizzes por módulo */
  useEffect(() => {
    if (!course?.modules) return;
    let mounted = true;
    (async () => {
      const q = {};
      for (const m of course.modules) {
        if (!m.has_quiz) continue;
        try {
          const r = await api.get(`/lessons/module/${m.id}/quiz/status`);
          if (!mounted) return;
          q[m.id] = r.data.approved || false;
        } catch {
          q[m.id] = false;
        }
      }
      if (mounted) setQuizApproved(q);
    })();
    return () => { mounted = false; };
  }, [course]);

  /* Scroll/Video completion logic */
  useEffect(() => {
    setCanFinish(false); setIsAutoVideoDone(false);
    const lesson = course?.modules?.[activeModule]?.lessons?.[activeLesson];
    if (!lesson) return;

    const type = lesson.content_type?.toLowerCase();

    if (completedLessons[lesson.id]) { setCanFinish(true); return; }
    if (type === "video" || type === "youtube") return;

    const box = lessonContentRef.current;
    if (!box) return;

    const onScroll = () => {
      const percent = ((box.scrollTop + box.clientHeight) / box.scrollHeight) * 100;
      if (percent >= 85) {
        setCanFinish(true);
        box.removeEventListener("scroll", onScroll);
      }
    };
    box.addEventListener("scroll", onScroll);
    return () => box.removeEventListener("scroll", onScroll);
  }, [activeModule, activeLesson, course, completedLessons]);

  const checkLessonProgress = useCallback(async (lessonId) => {
    try {
      const res = await api.get(`/lessons/${lessonId}/progress`);
      const isCompleted = res.data && res.data.percent >= 100;
      if (isCompleted) setCompletedLessons(prev => ({ ...prev, [lessonId]: true }));
      return isCompleted;
    } catch (e) {
      console.error("Erro ao checar progresso:", e);
      return false;
    }
  }, []);

  /* Finalize lesson */
  const finalizeLesson = async () => {
    const mod = course.modules[activeModule];
    const lesson = mod.lessons[activeLesson];
    const isLast = activeLesson === mod.lessons.length - 1;
    const ok = await completeLesson(lesson.id);
    if (ok) {
      setCompletedLessons(prev => ({ ...prev, [lesson.id]: true }));
      setShowCompletionModal(true);
      setCanFinish(true);

      if (mod.has_quiz && isLast && !quizApproved[mod.id]) {
        // show hint toast and play sound
        setShowQuizHint(true);
        try {
          new Audio("/uploads/sons/notify.mp3").play();
        } catch (e) { /* ignore play errors */ }
      }
    }
  };

  const onVideoEnd = async () => {
    setIsAutoVideoDone(true);
    await finalizeLesson();
  };

  const openQuiz = () => {
    setShowQuiz(true);
    setShowQuizHint(false);
  };

  /* Auto-open quiz after 5s if available and not opened */
  useEffect(() => {
    if (!course) return;
    const mod = course.modules[activeModule];
    const lesson = mod.lessons[activeLesson];
    const isLast = activeLesson === mod.lessons.length - 1;
    const available = mod.has_quiz && completedLessons[lesson.id] && !quizApproved[mod.id];

    if (available && !showQuiz) {
      const t = setTimeout(() => {
        openQuiz();
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [course, activeModule, activeLesson, completedLessons, quizApproved, showQuiz]);

  const selectLesson = async (m, l) => {
    const nextId = course.modules[m].lessons[l].id;
    if (l > 0) {
      const prev = course.modules[m].lessons[l - 1].id;
      if (!completedLessons[prev]) return; // sequence lock
    }
    setActiveModule(m);
    setActiveLesson(l);
    setShowQuiz(false);
    setShowQuizHint(false);
    setCanFinish(!!completedLessons[nextId]);

    if (!completedLessons[nextId]) {
      const done = await checkLessonProgress(nextId);
      if (done) setCanFinish(true);
    }
  };

  /* ============= RENDER & LAYOUT ============= */
  if (loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center bg-slate-950">
          <Spinner />
        </div>
      </AppLayout>
    );
  }

  if (!course || !course.modules?.length) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center text-white">
          Nenhum conteúdo disponível neste curso.
        </div>
      </AppLayout>
    );
  }

  const currentModule = course.modules[activeModule];
  const currentLesson = currentModule.lessons[activeLesson];
  const isVideo = ["video", "youtube"].includes(currentLesson.content_type?.toLowerCase());
  const isLessonCompleted = completedLessons[currentLesson.id];
  const isModuleQuizApproved = quizApproved[currentModule.id];
  const isLastLessonOfModule = activeLesson === currentModule.lessons.length - 1;

  const quizAvailable = currentModule.has_quiz && isLastLessonOfModule && isLessonCompleted && !isModuleQuizApproved;

/* ============================================================
   TRECHO ATUALIZADO DENTRO DA FUNÇÃO renderLessonContent
   ============================================================*/

const renderLessonContent = () => {
  const type = currentLesson.content_type?.toLowerCase();
  const url = fileURL(currentLesson.content);
  const isCompleted = completedLessons[currentLesson.id];

  // 🚩 TRAVA DE SEGURANÇA (Otimizada)
  if (isCompleted) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-slate-900/80 border border-emerald-500/30 rounded-2xl text-center">
        <CheckCircleIcon className="w-16 h-16 text-emerald-400 mb-4" />
        <h2 className="text-xl font-bold text-white">Conteúdo Concluído!</h2>
        <p className="text-slate-400 mt-2 mb-6">Você já finalizou esta aula.</p>
        <button onClick={() => {
             // Lógica para pular para próxima aula se desejar
        }} className="bg-emerald-500 text-slate-950 px-6 py-2 rounded-full font-bold hover:bg-emerald-400 transition">
          Próxima Aula
        </button>
      </div>
    );
  }

  // 1. VÍDEO DE UPLOAD (Ajustado para proporção 16:9)
  if (type === "video" || type === "video_upload" || type === "videoupload") {
    return (
      <div className="w-full aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
        <VideoUploadPlayer lesson={currentLesson} onFinish={onVideoEnd} />
      </div>
    );
  }

  // 2. YOUTUBE / EMBED (Ajustado para proporção 16:9)
  if (type === "youtube" || type === "video_embed") {
    return (
      <div className="w-full aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
        <YouTubeEmbed url={url} onFinish={onVideoEnd} />
      </div>
    );
  }

  // 3. APRESENTAÇÃO DE PPTX
  if (type === "pptx") {
    const pptxPreviewUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    return (
      <div className="w-full aspect-video bg-slate-800 rounded-2xl overflow-hidden border border-slate-700">
        <iframe 
          src={pptxPreviewUrl} 
          width="100%" 
          height="100%" 
          frameBorder="0" 
          title="Apresentação PowerPoint"
        />
      </div>
    );
  }

  // 4. PDF (Mantém altura fixa pois PDF não tem proporção de vídeo)
  if (type === "pdf" || type === "word" || type === "download") {
    const src = (type === "word" || type === "download")
      ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
      : url;
    return (
      <iframe 
        src={src} 
        title="Material da Aula" 
        className="w-full h-[75vh] border border-slate-800 rounded-2xl" 
      />
    );
  }

  // 5. FALLBACK
  return (
    <div className="p-8 text-center rounded-xl bg-slate-800/50 border border-slate-700">
      <p className="text-amber-200 mb-4">Tipo de conteúdo: <b>{currentLesson.content_type}</b></p>
      <a href={url} target="_blank" rel="noreferrer" className="px-6 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-full text-white font-bold transition">
        Abrir arquivo manualmente
      </a>
    </div>
  );
};

  /* MAIN JSX */
  return (
    <AppLayout>
      <div className="min-h-screen bg-slate-950 text-white flex">

        {/* SIDEBAR */}
        <aside className="w-72 bg-slate-900/60 border-r border-slate-800 overflow-y-auto">
          <div className="p-5 border-b border-slate-800">
            <h1 className="text-lg font-bold">{course.title}</h1>
            <p className="text-xs text-slate-400">{course.modules.length} módulos • {course.modules.reduce((a,m)=>a+m.lessons.length,0)} aulas</p>
          </div>

          <div className="p-4 space-y-4">
            {course.modules.map((m, mi) => {
              const moduleDone = m.has_quiz ? quizApproved[m.id] : m.lessons.every(a => completedLessons[a.id]);
              return (
                <div key={m.id} className="bg-slate-900/80 rounded-xl border border-slate-800">
                  <div className="px-4 py-2 flex justify-between items-center">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Módulo {mi+1}</p>
                      <h2 className="text-sm font-semibold">{m.title}</h2>
                    </div>
                    {moduleDone ? <CheckCircleIcon className="w-5 text-emerald-400" /> : <LockClosedIcon className="w-5 text-slate-600" />}
                  </div>

                  <div>
                    {m.lessons.map((l, li) => {
                      const done = completedLessons[l.id] || moduleDone;
                      const active = activeModule === mi && activeLesson === li;
                      const locked = li > 0 && !completedLessons[m.lessons[li-1].id];
                      return (
                        <button
                          key={l.id}
                          disabled={locked}
                          onClick={() => selectLesson(mi, li)}
                          className={`w-full flex items-center gap-3 px-4 py-2 text-left text-xs ${active ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/40"} ${locked ? "opacity-40 cursor-not-allowed":"cursor-pointer"}`}
                        >
                          <div className="w-7 h-7 flex items-center justify-center rounded bg-slate-950">
                            {done ? <CheckCircleIcon className="w-4 text-emerald-400" /> : locked ? <LockClosedIcon className="w-4 text-slate-500" /> : <PlayIcon className="w-4" />}
                          </div>
                          <span className="flex-1 text-xs">{l.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* CONTENT */}
        <main className="flex-1 flex flex-col">
          {/* HEADER */}
         <header className="p-6 border-b border-slate-500 bg-[#060924] flex justify-between items-center">
            <div>
              <p className="text-xs text-slate-500">Módulo {activeModule+1}</p>
              <h1 className="text-xl font-bold">{currentLesson.title}</h1>
            </div>

            {/* QUIZ BUTTON + TOAST (fixed above header) */}
            {currentModule.has_quiz && (
              <div className="relative flex items-center">
                {/* Toast balloon fixed above everything (high z) */}
                {quizAvailable && !isModuleQuizApproved && !showQuiz && showQuizHint && (
                  <div className="fixed top-20 right-8 z-[999999] pointer-events-auto">
                    <div className="bg-emerald-400 text-slate-900 text-sm px-4 py-2 rounded-full shadow-2xl animate-bounce font-semibold">
                      Quiz disponível! 🚀
                    </div>
                  </div>
                )}

                {/* Quiz button - disabled when blocked (blocked state handled in modal too) */}
                <button
                  onClick={() => { if (quizAvailable && !isModuleQuizApproved) openQuiz(); }}
                  disabled={!quizAvailable || isModuleQuizApproved}
                  className={`px-4 py-2 rounded-full text-xs font-bold transition ${isModuleQuizApproved ? "bg-emerald-600/40 text-slate-300" : quizAvailable ? "bg-indigo-600 text-yellow-200 hover:bg-indigo-500 animate-pulse shadow-[0_0_18px_#4f46e5]" : "bg-slate-800 text-slate-500 cursor-not-allowed"} hover:-translate-y-0.5 duration-200`}
                >
                  {isModuleQuizApproved ? "Quiz concluído" : quizAvailable ? "Iniciar Quiz" : "Conclua o módulo para liberar"}
                </button>
              </div>
            )}
          </header>

          {/* BODY */}
          <section className="p-6 flex-1 overflow-y-auto">
            <div ref={lessonContentRef} className={`rounded-2xl border border-slate-800 bg-slate-900/70 ${!isVideo ? "p-4 overflow-y-auto max-h-[75vh]" : "p-0"}`}>
              {renderLessonContent()}
            </div>

            {/* Conclude button for non-video */}
            {!isVideo && !isLessonCompleted && (
              <div className="flex justify-between items-center mt-4">
                <span className="text-xs text-slate-400">{canFinish ? "Pode concluir a aula!" : "Role até o fim para liberar ⚠"}</span>
                <button onClick={finalizeLesson} disabled={!canFinish} className={`px-6 py-3 rounded-full text-sm font-bold transition relative ${canFinish ? "bg-emerald-500 text-slate-900 hover:bg-emerald-400 animate-pulse shadow-[0_0_15px_#10ffb340]" : "bg-slate-800 text-slate-500 cursor-not-allowed"}`}>
                  {canFinish ? "🎉 Marcar aula como concluída" : "Role até o fim para liberar"}
                </button>
              </div>
            )}

            {/* Completed notice */}
            {(isLessonCompleted || (isVideo && isAutoVideoDone)) && (
              <div className="mt-4 p-3 bg-emerald-600/10 border border-emerald-700/50 rounded-xl text-xs text-slate-300">
                ✅ Esta aula já foi concluída!
              </div>
            )}

            {/* Comments + Chat */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              <LessonComments lessonId={currentLesson.id} lessonTitle={currentLesson.title} />
              <LessonChat courseId={courseId} />
            </div>
          </section>
        </main>

        {/* Quiz Modal */}
        <QuizModal
          show={showQuiz}
          onClose={() => setShowQuiz(false)}
          moduleId={currentModule.id}
          moduleTitle={currentModule.title}
          onModuleApproved={(id) => setQuizApproved(prev => ({ ...prev, [id]: true }))}
        />

        {/* Completion modal */}
        <CompletionModal show={showCompletionModal} onClose={() => setShowCompletionModal(false)} lessonTitle={currentLesson.title} />
      </div>
    </AppLayout>
  );
}

/* ============================================================
MODAL DE CONCLUSÃO
============================================================*/
function CompletionModal({ show, onClose, lessonTitle }) {
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [show, onClose]);

  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[99990] flex items-center justify-center bg-black/70 backdrop-blur">
      <div className="bg-slate-900 border border-emerald-500 rounded-2xl w-full max-w-sm p-8 text-center shadow-2xl">
        <CheckCircleIcon className="w-16 h-16 text-emerald-400 mx-auto mb-4 animate-bounce" />
        <h3 className="text-xl font-bold text-slate-50 mb-2">Aula Concluída! 🎉</h3>
        <p className="text-sm text-slate-300 mb-4">{lessonTitle}</p>
        <div className="text-xs text-slate-500">Seu progresso foi salvo. Este aviso será fechado automaticamente.</div>
      </div>
    </div>
  );
}

/* ============================================================
STAR ICON
============================================================*/
function StarIcon({ filled, index, onClick }) {
  return (
    <svg onClick={onClick ? () => onClick(index) : null} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
      className={`w-5 h-5 cursor-pointer transition ${filled ? "text-yellow-400" : "text-slate-700 hover:text-yellow-300"}`} fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2l2.9 6.1L22 9.3l-5 4.8L18 22l-6-3.2L6 22l1-7.9-5-4.8 7.1-1.2L12 2z" />
    </svg>
  );
}

/* ============================================================
COMMENTS
============================================================*/
function LessonComments({ lessonId, lessonTitle }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [rating, setRating] = useState(0);
  const [loading, setLoading] = useState(true);

  const API = `/lessons/${lessonId}/comments`;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get(API);
        if (!mounted) return;
        setComments(r.data.comments || []);
      } catch (e) { console.error(e); }
      if (mounted) setLoading(false);
    })();
    return () => { mounted = false; };
  }, [lessonId]);

  const send = async () => {
    if (!rating) return alert("Selecione uma quantidade de estrelas antes de enviar.");
    try {
      const r = await api.post(API, { message: text, rating: rating * 2 });
      setComments(prev => [{ id: r.data.comment?.id || Date.now(), user_name: user.name, message: text, rating: rating * 2, sent_at: r.data.comment?.sent_at || new Date().toISOString(), replies: [] }, ...prev]);
      setText(""); setRating(0);
    } catch (e) { alert("Erro ao enviar comentário"); }
  };

  const StarsSelector = () => (
    <div className="flex gap-1 my-2 items-center">
      {Array.from({ length: 5 }).map((_, i) => <StarIcon key={i} filled={i < rating} index={i + 1} onClick={(n) => setRating(n)} />)}
      {rating > 0 && <span className="text-xs text-yellow-300 ml-2">{rating}/5</span>}
    </div>
  );

  const StarsDisplay = (value) => (
    <div className="flex gap-1">{Array.from({ length: 5 }).map((_, i) => <StarIcon key={i} filled={i < value / 2} />)}</div>
  );

  if (!user) return null;

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 text-white">
      <h2 className="font-bold mb-1">Avaliações — {lessonTitle}</h2>

      <div className="mb-4">
        <StarsSelector />
        <textarea className="w-full p-2 text-sm bg-slate-900 border border-slate-700 rounded-lg" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Escreva um comentário..." />
        <button onClick={send} disabled={!rating} className={`mt-2 px-4 py-2 rounded-full text-xs font-bold ${rating ? "bg-indigo-600 hover:bg-indigo-500" : "bg-slate-700 cursor-not-allowed"}`}>Enviar avaliação</button>
      </div>

      <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
        {loading && <p className="text-xs text-slate-400">Carregando...</p>}
        {!loading && comments.length === 0 && <p className="text-xs text-slate-400">Nenhum comentário ainda.</p>}

        {comments.map(c => {
          const date = c.sent_at || c.created_at;
          const sentDate = date && typeof date === "string" ? parseISO(date) : date;
          const userName = c.user_name || "Aluno(a)";
          return (
            <div key={c.id} className="border border-slate-700 p-3 rounded-xl text-xs">
              <div className="flex justify-between items-start gap-2">
                <div className="flex flex-col">
                  <strong className="text-sm text-slate-50">{userName}</strong>
                  <span className="text-[10px] text-slate-400 mt-1">{sentDate ? format(sentDate, "dd/MM/yyyy HH:mm", { locale: ptBR }) : "Data indisponível"}</span>
                </div>
                {StarsDisplay(c.rating)}
              </div>
              {c.message && <p className="text-xs text-slate-300 mt-2">Mensagem: {c.message}</p>}
              {c.replies?.map(r => (
                <div key={r.id} className="mt-2 p-2 bg-indigo-500/10 border border-indigo-500/40 rounded-lg">
                  <strong className="text-xs text-indigo-300">Resposta da equipe:</strong>
                  <p className="text-xs text-slate-100">{r.reply}</p>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
CHAT
============================================================*/
function LessonChat({ courseId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    api.get(`/courses/${courseId}/chat`).then(r => { if (mounted) setMessages(r.data.messages || []); }).catch(() => { });
    return () => { mounted = false; };
  }, [courseId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (!user) return null;

  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    const r = await api.post(`/courses/${courseId}/chat`, { message: msg });
    setMessages(p => [...p, r.data.message]);
    setMsg("");
  };

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 text-white h-full flex flex-col">
      <h2 className="font-bold text-sm mb-2">Chat</h2>

      <div className="flex-1 overflow-y-auto pr-1 space-y-2 text-xs">
        {messages.map(m => (
          <div key={m.id} className="border-b border-slate-700 pb-1">
            <b>{m.user_name}</b> • {new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
            <p className="mt-1 text-slate-200">{m.message}</p>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="mt-2 flex gap-2">
        <input className="flex-1 p-2 rounded-lg bg-slate-900 border border-slate-700 text-xs" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Digite..." />
        <button className="px-4 rounded-lg bg-emerald-500 text-black font-bold">Enviar</button>
      </form>
    </div>
  );
}

/* ============================================================
QUIZ MODAL (com tentativas, bloqueio, shuffle, auto-close)
============================================================*/
function QuizModal({ show, onClose, moduleId, moduleTitle, onModuleApproved }) {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]); // each question: { id, question_text, options: [] }
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  // Attempts storage keys per module
  const LS_KEY = `quiz_attempts_${moduleId}`;
  const BLOCK_KEY = `quiz_block_${moduleId}`;

  const [attempts, setAttempts] = useState(0);
  const [blockedUntil, setBlockedUntil] = useState(null);
  const [remainingMs, setRemainingMs] = useState(0);

  // Load attempts and block state on mount / moduleId change
  useEffect(() => {
    if (!moduleId) return;
    const a = parseInt(localStorage.getItem(LS_KEY) || "0", 10);
    const b = parseInt(localStorage.getItem(BLOCK_KEY) || "0", 10);
    setAttempts(a || 0);
    if (b && b > Date.now()) {
      setBlockedUntil(b);
      setRemainingMs(b - Date.now());
    } else {
      setBlockedUntil(null);
      setRemainingMs(0);
    }
  }, [moduleId]); // safe: moduleId stable inside modal usage

  // Countdown for blocked state
  useEffect(() => {
    if (!blockedUntil) { setRemainingMs(0); return; }
    const t = setInterval(() => {
      const rem = blockedUntil - Date.now();
      if (rem <= 0) {
        clearInterval(t);
        setBlockedUntil(null);
        setAttempts(0);
        localStorage.removeItem(LS_KEY);
        localStorage.removeItem(BLOCK_KEY);
        setRemainingMs(0);
      } else {
        setRemainingMs(rem);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [blockedUntil]);

  // load and shuffle questions whenever modal opens (and not blocked)
  useEffect(() => {
    if (!show) return;
    if (blockedUntil && blockedUntil > Date.now()) return; // blocked - don't load
    let mounted = true;
    setLoading(true);
    setResult(null);

    api.get(`/lessons/module/${moduleId}/quiz`).then(r => {
      if (!mounted) return;
      const raw = r.data.questions || [];
      // shuffle questions and shuffle each options array
      const shuffled = shuffleArray(raw).map(q => ({
        ...q,
        options: shuffleArray(q.options || [])
      }));
      setQuestions(shuffled);
      setAnswers(new Array(shuffled.length).fill(null));
    }).catch(e => {
      console.error("Erro ao carregar quiz", e);
      setQuestions([]);
      setAnswers([]);
    }).finally(() => {
      if (mounted) setLoading(false);
    });

    return () => { mounted = false; };
  }, [show, moduleId, blockedUntil]);

  // Auto-close if user doesn't interact within 30s (only when modal is open and not result)
  useEffect(() => {
    if (!show) return;
    if (result) return;
    const t = setTimeout(() => {
      onClose();
    }, 30000); // 30s
    return () => clearTimeout(t);
  }, [show, result, onClose]);

  // Helper to format remainingMs into mm:ss
  const formatMs = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60).toString().padStart(2, "0");
    const s = (total % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  // Submit answers
  const submit = async () => {
    // check block
    if (blockedUntil && blockedUntil > Date.now()) {
      alert(`Você está bloqueado. Tente novamente em ${formatMs(blockedUntil - Date.now())}`);
      return;
    }

    setLoading(true);
    try {
      const r = await api.post(`/lessons/module/${moduleId}/quiz/submit`, { answers });
      setResult(r.data);

      // If approved
      if (r.data.approved) {
        // reset attempts storage
        localStorage.removeItem(LS_KEY);
        localStorage.removeItem(BLOCK_KEY);
        setAttempts(0);
        onModuleApproved(moduleId);
        // close after short delay to show result
        setTimeout(() => onClose(), 1500);
        return;
      }

      // Not approved: increment attempts
      const newAttempts = (attempts || 0) + 1;
      setAttempts(newAttempts);
      localStorage.setItem(LS_KEY, String(newAttempts));

      if (newAttempts >= 3) {
        const blockTime = Date.now() + 30 * 60 * 1000; // 30 minutes
        setBlockedUntil(blockTime);
        localStorage.setItem(BLOCK_KEY, String(blockTime));
        alert("Você errou 3 vezes. Tente novamente em 30 minutos.");
        // close modal after failure & block
        setTimeout(() => onClose(), 500);
        return;
      }

      // if not yet reached 3 attempts, close modal to allow reopening later (with shuffled questions)
      setTimeout(() => onClose(), 800);
    } catch (e) {
      console.error("Erro ao enviar quiz:", e);
      alert("Erro ao enviar respostas. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

// If blocked, show message instead of questions
if (!show) return null;

return (
  <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
    <div className="w-full max-w-3xl">
      <div className="bg-[#0f172a] border border-indigo-500/30 rounded-3xl shadow-2xl overflow-hidden">

        {/* HEADER */}
        <div className="px-6 py-4 border-b border-slate-700 flex justify-between items-center bg-gradient-to-r from-indigo-600/20 to-purple-600/20">
          <div>
            <h2 className="text-xl font-bold text-white">{moduleTitle}</h2>
            <p className="text-sm text-indigo-300">
              Responda todas as perguntas para concluir o módulo
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white text-lg"
          >
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 max-h-[65vh] overflow-y-auto space-y-6">

          {loading && (
            <div className="text-center text-slate-300">
              Carregando perguntas...
            </div>
          )}

          {/* BLOQUEADO */}
          {blockedUntil && blockedUntil > Date.now() ? (
            <div className="p-6 rounded-2xl bg-red-900/20 border border-red-500/40 text-center">
              <p className="text-red-300 font-bold text-lg">
                Você está bloqueado 🚫
              </p>
              <p className="text-slate-300 mt-2 text-sm">
                Aguarde para tentar novamente
              </p>
              <div className="mt-4 text-2xl font-mono text-red-200">
                {formatMs(remainingMs)}
              </div>
            </div>
          ) : (
            <>
              {!loading && questions.map((q, i) => (
                <div
                  key={q.id || i}
                  className="bg-slate-800/60 border border-slate-700 rounded-2xl p-5"
                >
                  <p className="text-white font-semibold text-base mb-4">
                    {i + 1}. {q.question_text}
                  </p>

                  <div className="space-y-3">
                    {q.options.map((opt, oi) => {
                      const selected = answers[i] === opt;

                      return (
                        <button
                          key={oi}
                          onClick={() =>
                            setAnswers((a) => {
                              const copy = [...a];
                              copy[i] = opt; // 🔥 SALVA O VALOR REAL
                              return copy;
                            })
                          }
                          className={`w-full text-left px-4 py-3 rounded-xl border transition-all duration-200
                          ${
                            selected
                              ? "bg-indigo-600 border-indigo-400 text-white shadow-lg"
                              : "bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* RESULTADO */}
              {result && (
                <div
                  className={`p-4 rounded-xl text-center font-semibold text-sm
                  ${
                    result.approved
                      ? "bg-emerald-600/20 text-emerald-300 border border-emerald-500/40"
                      : "bg-red-600/20 text-red-300 border border-red-500/40"
                  }`}
                >
                  {result.approved ? "✅ Aprovado!" : "❌ Não alcançou a nota"}
                  <div className="mt-1 text-xs">
                    {result.percentage}% • {result.correct}/{result.total}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t border-slate-700 flex justify-between items-center bg-slate-900">
          <div className="text-xs text-slate-400">
            {attempts > 0 && !blockedUntil && (
              <span>Tentativas: {attempts}/3</span>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 rounded-xl text-slate-300"
            >
              Fechar
            </button>

            {!blockedUntil && (
              <button
                onClick={submit}
                disabled={loading || questions.length === 0}
                className="px-5 py-2 text-sm font-bold bg-indigo-600 hover:bg-indigo-500 rounded-xl text-white shadow-lg transition"
              >
                Enviar respostas
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

}
